/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';

import BookCard from 'components/cards/entities/BookCard';
import EventCard from 'components/cards/entities/EventCard';
import InstituteCard from 'components/cards/entities/InstituteCard';
import MovieCard from 'components/cards/entities/MovieCard';
import ObjectCard from 'components/cards/entities/ObjectCard';
import PersonCard from 'components/cards/entities/PersonCard';
import PoemCard from 'components/cards/entities/PoemCard';
import PromoBlockCard from 'components/cards/entities/PromoBlockCard';
import PublicationCard from 'components/cards/entities/PublicationCard';

import StyledContentAudio from './StyledContentAudio';
import StyledContentColumns from './StyledContentColumns';
import StyledContentCut from './StyledContentCut';
import StyledContentDetailedSlider from './StyledContentDetailedSlider';
import StyledContentGallery from './StyledContentGallery';
import StyledContentGroup from './StyledContentGroup';
import StyledContentHeader from './StyledContentHeader';
import StyledContentHtml from './StyledContentHtml';
import StyledContentOrderedList from './StyledContentOrderedList';
import StyledContentQuote from './StyledContentQuote';
import StyledContentRecommendation from './StyledContentRecommendation';
import StyledContentSingleCard from './StyledContentSingleCard';
import StyledContentText from './StyledContentText';
import StyledContentLinks from './StyledContentLinks';
import StyledContentSpoiler from './StyledContentSpoiler';
import StyledContentTabs from './StyledContentTabs';
import StyledContentVideo from './StyledContentVideo';
import StyledContentImage from './StyledContentImage';
import StyledContentFrame from './StyledContentFrame';
import StyledContentInteractive from './StyledContentInteractive';
import StyledContentNebBook from './StyledContentNebBook';

const StyledContentBlock = ({
	type,
	getSpoilerRefs,
	getHeaderRefs,
	onSpoilerClick,
	getSpoilerContentHeight,
	activeSpoiler,
	blockKey,
	isInteractive,
	...props
}) => {
	const paramsCardLandscape = { width: 392, height: 282 };
	const paramsCardPortrait = { width: 240, height: 321 };
	let paramsCardMovie = paramsCardLandscape;

	const isMovieCardPortrait = ['movie', 'performance'].includes(
		props[type]?.type
	);

	if (isMovieCardPortrait) {
		paramsCardMovie = paramsCardPortrait;
	}

	switch (type) {
		case 'audio':
			return isInteractive ? (
				<StyledContentAudio audio={props.file} isInteractive={isInteractive} />
			) : (
				<StyledContentAudio {...props} />
			);
		case 'video':
			return (
				<StyledContentVideo
					isInteractive={isInteractive}
					{...props}
					autoPlay={isInteractive ? false : props?.autoPlay}
				/>
			);
		case 'columns':
			return <StyledContentColumns {...props} />;
		case 'detailedSlider':
			return <StyledContentDetailedSlider {...props} />;
		case 'cut':
			return <StyledContentCut {...props} />;
		case 'gallery':
			return <StyledContentGallery {...props} />;
		case 'group':
			return <StyledContentGroup {...props} />;
		case 'header':
			return (
				<StyledContentHeader
					blockKey={blockKey}
					getRefs={getHeaderRefs}
					{...props}
				/>
			);
		case 'html':
			return <StyledContentHtml {...props} />;
		case 'line':
			return <hr />;
		case 'orderedList':
			return <StyledContentOrderedList list={props} />;
		case 'quote':
			return <StyledContentQuote {...props} />;
		case 'recommendation':
			return <StyledContentRecommendation {...props} />;
		case 'text':
			return <StyledContentText {...props} />;
		case 'frame':
			return <StyledContentFrame {...props} />;
		case 'image':
			return (
				<StyledContentImage
					isInteractive={isInteractive}
					{...props}
					lazy={isInteractive ? false : props?.lazy}
				/>
			);
		case 'links':
			return <StyledContentLinks linksList={props} />;
		case 'spoiler':
			return (
				<StyledContentSpoiler
					data={{ header: props.header, content: props.content }}
					getSpoilerRefs={getSpoilerRefs}
					onSpoilerClick={onSpoilerClick}
					getSpoilerContentHeight={getSpoilerContentHeight}
					activeSpoiler={activeSpoiler}
					blockKey={blockKey}
				/>
			);
		case 'tabs':
			return <StyledContentTabs items={props.items} />;
		case 'article':
		case 'theme':
			return (
				<StyledContentSingleCard>
					<PublicationCard entity={props[type]} params={paramsCardLandscape} />
				</StyledContentSingleCard>
			);
		case 'book':
			return (
				<StyledContentSingleCard orientation="portrait">
					<BookCard entity={props[type]} colorIndex={0} keepRatio cropText />
				</StyledContentSingleCard>
			);
		case 'event':
			return (
				<StyledContentSingleCard>
					<EventCard entity={props[type]} params={paramsCardLandscape} />
				</StyledContentSingleCard>
			);
		case 'institute':
			return (
				<StyledContentSingleCard>
					<InstituteCard entity={props[type]} params={paramsCardLandscape} />
				</StyledContentSingleCard>
			);
		case 'movie':
			return (
				<StyledContentSingleCard
					orientation={isMovieCardPortrait ? 'portrait' : 'landscape'}
				>
					<MovieCard entity={props[type]} params={paramsCardMovie} />
				</StyledContentSingleCard>
			);
		case 'object':
			return (
				<StyledContentSingleCard>
					<ObjectCard entity={props[type]} params={paramsCardLandscape} />
				</StyledContentSingleCard>
			);
		case 'person':
			return (
				<StyledContentSingleCard orientation="portrait">
					<PersonCard entity={props[type]} params={paramsCardPortrait} />
				</StyledContentSingleCard>
			);
		case 'poem':
			return (
				<StyledContentSingleCard>
					<PoemCard entity={props[type]} />
				</StyledContentSingleCard>
			);
		case 'promoBlock':
			return (
				<StyledContentSingleCard>
					<PromoBlockCard entity={props[type]} params={paramsCardPortrait} />
				</StyledContentSingleCard>
			);
		case 'interactive':
			return <StyledContentInteractive {...props} />;
		case 'nebBook':
			return <StyledContentNebBook bookId={props?.bookId} />;
		default:
			return null;
	}
};

StyledContentBlock.propTypes = {
	type: PropTypes.oneOf([
		'audio',
		'video',
		'columns',
		'cut',
		'detailedSlider',
		'gallery',
		'cut',
		'group',
		'header',
		'html',
		'line',
		'orderedList',
		'quote',
		'split',
		'recommendation',
		'tabs',
		'links',
		'text',
		'article',
		'book',
		'event',
		'institute',
		'theme',
		'movie',
		'object',
		'person',
		'poem',
		'promoBlock',
		'spoiler',
		'text',
		'image',
		'frame',
		'interactive',
		'nebBook'
	]).isRequired,
	header: PropTypes.string,
	content: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	getSpoilerRefs: PropTypes.func,
	getHeaderRefs: PropTypes.func,
	onSpoilerClick: PropTypes.func,
	getSpoilerContentHeight: PropTypes.func,
	activeSpoiler: PropTypes.string,
	blockKey: PropTypes.string,
	items: PropTypes.array,
	bookId: PropTypes.string,
	file: PropTypes.object,
	src: PropTypes.string,
	material: PropTypes.object,
	isInteractive: PropTypes.bool,
	lazy: PropTypes.bool,
	autoPlay: PropTypes.bool
};

export default StyledContentBlock;
